import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EventClickArg, EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import { MdAdd, MdChevronLeft, MdChevronRight } from "react-icons/md";
import OmniCalendar from "components/calendar/OmniCalendar";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import FilterChip from "components/ui/FilterChip";
import {
  getSessionStatusBadgeClass,
  getSessionStatusEventColor,
} from "constants/colors";
import { useLanguage } from "context/LanguageContext";
import { classMgmtService } from "services/admin/classMgmtService";
import type {
  ClassListItem,
  ClassSession,
  SessionParticipant,
  SessionWithDetails,
} from "types/domain";
import { addDays, formatWeekRangeLabel, getWeekStart } from "utils/weekDate";
import SessionDetailModal, {
  SessionDetailTarget,
} from "views/user/classes/SessionDetailModal";

type Translate = (
  key: string,
  params?: Record<string, string | number>
) => string;

type SessionWithClass = SessionWithDetails & {
  classId: string;
  className: string;
  classCode: string | null;
};

const toFriendlyError = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    if (
      error.message === "Load failed" ||
      error.message.includes("Failed to fetch")
    ) {
      return "sessionHub.connectionFailed";
    }
    return error.message;
  }
  return fallback;
};

// Prefer human-readable venue name/address over the raw location string,
// which for some legacy sessions was mistakenly overwritten with GPS
// coordinates (see backend fix in session_service.py).
const displayLocation = (session: SessionWithClass): string | null =>
  session.venue?.name || session.venue?.address || session.location || null;

const startOfDay = (date: Date) => {
  const clone = new Date(date);
  clone.setHours(0, 0, 0, 0);
  return clone;
};

const toDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fromDateInput = (value: string) => {
  const [yearStr, monthStr, dayStr] = value.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const dayKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;

const formatDayLabel = (date: Date, locale: string) =>
  date.toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const enumerateDays = (start: Date, end: Date) => {
  const days: Date[] = [];
  const cursor = startOfDay(start);
  const limit = startOfDay(end);

  while (cursor <= limit) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

// CLOSED is shown as COMPLETED on purpose: both mean the session is over.
const statusBadgeKey = (status: ClassSession["status"]) => {
  if (status === "ACTIVE") return "enum.sessionStatus.ACTIVE";
  if (status === "COMPLETED" || status === "CLOSED")
    return "enum.sessionStatus.COMPLETED";
  if (status === "CANCELLED") return "enum.sessionStatus.CANCELLED";
  return "enum.sessionStatus.SCHEDULED";
};

const isCompletedLikeStatus = (status: ClassSession["status"]) =>
  status === "COMPLETED" || status === "CLOSED";

const formatClock = (value: string | null | undefined, locale: string) => {
  if (!value) return "--:--";
  return new Date(value).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTimeRange = (
  start: string | null | undefined,
  end: string | null | undefined,
  locale: string
) => {
  if (!start) return "--:-- - --:--";
  return `${formatClock(start, locale)} - ${formatClock(end, locale)}`;
};

const getParticipantName = (participant: SessionParticipant, t: Translate) =>
  participant.user?.full_name ||
  participant.user?.email ||
  t("sessionHub.noName");

const getTeacherNames = (participants: SessionParticipant[], t: Translate) => {
  const names = participants
    .filter((participant) => participant.role === "teacher")
    .map((participant) => getParticipantName(participant, t));
  return names.length > 0 ? names.join(", ") : t("sessionHub.unassigned");
};

const getTrainerNames = (participants: SessionParticipant[], t: Translate) => {
  const names = participants
    .filter((participant) => participant.role === "trainer")
    .map((participant) => getParticipantName(participant, t));
  return names.length > 0 ? names.join(", ") : t("sessionHub.unassigned");
};

const getStudentCount = (participants: SessionParticipant[]) =>
  participants.filter((participant) => participant.role === "student").length;

type ViewMode = "list" | "calendar";

const SessionHub = ({
  centerId,
  classBasePath,
  readOnly = false,
}: {
  centerId?: string;
  classBasePath?: string;
  // Renders a read-only variant for STUDENT callers: hides the "create
  // session" affordance and the student-count column (which the backend
  // redacts for STUDENT anyway), and opens the read-only SessionDetailModal
  // instead of navigating to the staff-only session edit route.
  readOnly?: boolean;
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, language } = useLanguage();
  const defaultClassBase = classBasePath ?? "/admin/classes-mgmt";
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedSession, setSelectedSession] =
    React.useState<SessionDetailTarget | null>(null);

  const [classes, setClasses] = React.useState<ClassListItem[]>([]);
  const [sessions, setSessions] = React.useState<SessionWithClass[]>([]);
  const [selectedClassId, setSelectedClassId] = React.useState<string>("all");
  const [selectedStatus, setSelectedStatus] = React.useState<
    "all" | ClassSession["status"]
  >("all");
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [viewMode, setViewMode] = React.useState<ViewMode>("list");
  const [showCreateSessionModal, setShowCreateSessionModal] =
    React.useState(false);

  const weekStart = React.useMemo(
    () => addDays(getWeekStart(new Date()), weekOffset * 7),
    [weekOffset]
  );
  const weekEnd = React.useMemo(() => addDays(weekStart, 6), [weekStart]);
  const fromDate = React.useMemo(() => toDateInput(weekStart), [weekStart]);
  const toDate = React.useMemo(() => toDateInput(weekEnd), [weekEnd]);

  const loadData = React.useCallback(async () => {
    const [classRes, feedRes] = await Promise.all([
      classMgmtService.getAll({ skip: 0, limit: 200, center_id: centerId }),
      classMgmtService.getSessionsFeed({
        date_from: fromDate ? `${fromDate}T00:00:00` : undefined,
        date_to: toDate ? `${toDate}T23:59:59` : undefined,
        limit: 200,
      }),
    ]);

    const classItems = classRes.items;
    setClasses(classItems);

    const classMap = new Map(classItems.map((c) => [c.id, c]));
    // Filter the feed down to sessions belonging to the loaded classes — needed
    // when scoped to a center (the backend feed does not accept center_id).
    const scopedFeed = centerId
      ? feedRes.items.filter(
          (session) => session.class_id && classMap.has(session.class_id)
        )
      : feedRes.items;

    const mapped: SessionWithClass[] = scopedFeed.map((session) => ({
      ...session,
      classId: session.class_id ?? "",
      // Empty when unknown; the fallback label is translated at render time.
      className: classMap.get(session.class_id ?? "")?.name ?? "",
      classCode: classMap.get(session.class_id ?? "")?.class_code ?? null,
    }));

    setSessions(mapped);
  }, [centerId, fromDate, toDate]);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadData();
      } catch (err: any) {
        setError(toFriendlyError(err, "sessionHub.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [loadData]);

  const rangeError = React.useMemo(() => {
    if (!fromDate || !toDate) return "sessionHub.rangeRequired";
    return fromDate > toDate ? "sessionHub.rangeInvalid" : null;
  }, [fromDate, toDate]);

  const sessionStatusOptions = React.useMemo(
    () => [
      { value: "all", label: t("sessionHub.allStatuses") },
      { value: "SCHEDULED", label: t("enum.sessionStatus.SCHEDULED") },
      { value: "ACTIVE", label: t("enum.sessionStatus.ACTIVE") },
      { value: "COMPLETED", label: t("enum.sessionStatus.COMPLETED") },
      { value: "CANCELLED", label: t("enum.sessionStatus.CANCELLED") },
    ],
    [t]
  );

  const filteredSessions = React.useMemo(() => {
    if (rangeError) return [];

    return sessions
      .filter((session) => {
        if (selectedClassId !== "all" && session.classId !== selectedClassId) {
          return false;
        }
        if (
          selectedStatus !== "all" &&
          !(selectedStatus === "COMPLETED"
            ? isCompletedLikeStatus(session.status)
            : session.status === selectedStatus)
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aStart = a.start_time ? new Date(a.start_time).getTime() : 0;
        const bStart = b.start_time ? new Date(b.start_time).getTime() : 0;
        return aStart - bStart;
      });
  }, [rangeError, selectedClassId, selectedStatus, sessions]);

  const sessionsByDay = React.useMemo(() => {
    if (rangeError) return [];

    const from = fromDateInput(fromDate);
    const to = fromDateInput(toDate);
    const days = enumerateDays(from, to);
    const grouped = new Map<string, SessionWithClass[]>();

    filteredSessions.forEach((session) => {
      if (!session.start_time) return;
      const key = dayKey(new Date(session.start_time));
      const items = grouped.get(key) ?? [];
      items.push(session);
      grouped.set(key, items);
    });

    return days.map((date) => {
      const key = dayKey(date);
      return {
        key,
        date,
        items: grouped.get(key) ?? [],
      };
    });
  }, [filteredSessions, fromDate, rangeError, toDate]);

  const todayKey = React.useMemo(() => dayKey(new Date()), []);

  const activeClasses = React.useMemo(
    () =>
      classes.filter(
        (item) => item.status === "OPENING" || item.status === "ONGOING"
      ),
    [classes]
  );

  const calendarEvents = React.useMemo<EventInput[]>(
    () =>
      filteredSessions.map((session) => ({
        id: session.id,
        title:
          session.title || session.className || t("sessionHub.unknownClass"),
        start: session.start_time || undefined,
        end: session.end_time || undefined,
        backgroundColor: getSessionStatusEventColor(session.status),
        borderColor: getSessionStatusEventColor(session.status),
        extendedProps: {
          classId: session.classId,
          className: session.className || t("sessionHub.unknownClass"),
        },
      })),
    [filteredSessions, t]
  );

  const openSessionDetail = React.useCallback((session: SessionWithClass) => {
    setSelectedSession({
      id: session.id,
      title: session.title,
      start_time: session.start_time,
      end_time: session.end_time,
      location: session.location,
      venueName: session.venue?.name || session.venue?.address || null,
    });
  }, []);

  const handleSessionClick = React.useCallback(
    (session: SessionWithClass) => {
      if (readOnly) {
        openSessionDetail(session);
        return;
      }
      navigate(
        `${defaultClassBase}/${session.classId}/sessions/${session.id}`,
        {
          state: { from: `${location.pathname}${location.search}` },
        }
      );
    },
    [
      defaultClassBase,
      location.pathname,
      location.search,
      navigate,
      openSessionDetail,
      readOnly,
    ]
  );

  const handleCalendarEventClick = React.useCallback(
    (info: EventClickArg) => {
      const classId = info.event.extendedProps?.classId as string | undefined;
      if (!classId) return;
      if (readOnly) {
        const session = filteredSessions.find((s) => s.id === info.event.id);
        if (session) openSessionDetail(session);
        return;
      }
      navigate(`${defaultClassBase}/${classId}/sessions/${info.event.id}`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [
      defaultClassBase,
      filteredSessions,
      location.pathname,
      location.search,
      navigate,
      openSessionDetail,
      readOnly,
    ]
  );

  const handleOpenCreateSession = React.useCallback(() => {
    setShowCreateSessionModal(true);
  }, []);

  const handleSelectCreateClass = React.useCallback(
    (classId: string) => {
      setShowCreateSessionModal(false);
      navigate(`${defaultClassBase}/${classId}/sessions/new`);
    },
    [defaultClassBase, navigate]
  );

  if (loading) {
    return (
      <div className="mt-5 text-sm text-ink-muted">
        {t("sessionHub.loading")}
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-ink">
          {t("sessionHub.title")}
        </h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-line bg-surface p-1">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                viewMode === "list"
                  ? "bg-brand-500 text-white"
                  : "text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              }`}
            >
              {t("sessionHub.viewList")}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("calendar")}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                viewMode === "calendar"
                  ? "bg-brand-500 text-white"
                  : "text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              }`}
            >
              {t("sessionHub.viewCalendar")}
            </button>
          </div>

          {!readOnly && (
            <button
              type="button"
              onClick={handleOpenCreateSession}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-600"
            >
              <MdAdd className="h-4 w-4" />
              {t("sessionHub.create")}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
          {t(error)}
        </div>
      )}

      <Card extra="w-full p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {t("sessionHub.class")}
            </label>
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm text-ink outline-none transition-colors focus:border-brand-500"
            >
              <option value="all">{t("sessionHub.allClasses")}</option>
              {classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {t("sessionHub.week")}
            </label>
            <div className="flex items-center justify-between gap-2 rounded-xl border border-line px-2 py-2">
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => setWeekOffset((value) => value - 1)}
                startIcon={<MdChevronLeft className="h-4 w-4" />}
              >
                {t("sessionHub.prevWeek")}
              </ActionButton>
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-sm font-medium text-ink">
                  {t("sessionHub.weekLabel", {
                    range: formatWeekRangeLabel(weekStart),
                  })}
                </span>
                {weekOffset !== 0 ? (
                  <ActionButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setWeekOffset(0)}
                  >
                    {t("sessionHub.thisWeek")}
                  </ActionButton>
                ) : null}
              </div>
              <ActionButton
                variant="ghost"
                size="sm"
                onClick={() => setWeekOffset((value) => value + 1)}
                endIcon={<MdChevronRight className="h-4 w-4" />}
              >
                {t("sessionHub.nextWeek")}
              </ActionButton>
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {sessionStatusOptions.map((option) => (
            <FilterChip
              key={option.value}
              active={selectedStatus === option.value}
              onClick={() =>
                setSelectedStatus(
                  option.value as "all" | ClassSession["status"]
                )
              }
            >
              {option.label}
            </FilterChip>
          ))}
        </div>

        {rangeError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
            {t(rangeError)}
          </div>
        )}

        {viewMode === "list" ? (
          <div className="mt-3 flex max-h-[680px] flex-col gap-2 overflow-auto pr-1">
            {sessionsByDay.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-3 py-6 text-center text-sm text-ink-muted">
                {t("sessionHub.emptyRange")}
              </p>
            ) : (
              sessionsByDay
                .filter(
                  (group) => group.items.length > 0 || group.key === todayKey
                )
                .map((group) => {
                  const isToday = group.key === todayKey;

                  return (
                    <div
                      key={group.key}
                      className={`rounded-2xl border p-3 ${
                        isToday
                          ? "border-brand-500 bg-brand-50/70 dark:border-brand-400 dark:bg-brand-500/10"
                          : "border-line bg-white dark:bg-surface/60"
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold capitalize text-ink">
                          {formatDayLabel(group.date, locale)}
                        </p>
                        {isToday ? (
                          <span className="rounded-full bg-brand-500 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                            {t("sessionHub.today")}
                          </span>
                        ) : null}
                      </div>

                      {group.items.length === 0 ? (
                        <p className="rounded-lg border border-dashed border-line px-2.5 py-2 text-sm text-ink">
                          {t("sessionHub.emptyDay")}
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {group.items.map((session) => (
                            <button
                              key={session.id}
                              type="button"
                              onClick={() => handleSessionClick(session)}
                              className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-left transition-colors hover:border-brand-400 hover:bg-brand-50/30 dark:hover:bg-brand-500/10"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-ink">
                                    {session.title || t("sessionHub.session")} /{" "}
                                    <span className="font-normal">
                                      {session.className ||
                                        t("sessionHub.unknownClass")}
                                      {session.classCode
                                        ? ` (${session.classCode})`
                                        : ""}
                                    </span>
                                  </p>
                                  {displayLocation(session) && (
                                    <p className="truncate text-[11px] text-ink-muted">
                                      {displayLocation(session)}
                                    </p>
                                  )}
                                </div>
                                <span
                                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getSessionStatusBadgeClass(
                                    session.status
                                  )}`}
                                >
                                  {t(statusBadgeKey(session.status))}
                                </span>
                              </div>

                              <div className="mt-1 flex items-center gap-2 text-[12px] text-ink">
                                <p
                                  className={`inline-flex shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${getSessionStatusBadgeClass(
                                    session.status
                                  )}`}
                                >
                                  {formatTimeRange(
                                    session.start_time,
                                    session.end_time,
                                    locale
                                  )}
                                </p>
                                {!readOnly && (
                                  <p className="shrink-0 whitespace-nowrap text-[12px] text-ink">
                                    {t("sessionHub.studentCount", {
                                      count: getStudentCount(
                                        session.participants
                                      ),
                                    })}
                                  </p>
                                )}
                                <p className="min-w-0 truncate text-[12px] text-ink">
                                  {t("sessionHub.teacherNames", {
                                    names: getTeacherNames(
                                      session.participants,
                                      t
                                    ),
                                  })}
                                </p>
                                <p className="min-w-0 truncate text-[12px] text-ink">
                                  {t("sessionHub.trainerNames", {
                                    names: getTrainerNames(
                                      session.participants,
                                      t
                                    ),
                                  })}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </div>
        ) : (
          <div className="mt-4">
            <OmniCalendar
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
                listPlugin,
              ]}
              initialView="timeGridWeek"
              locale={language}
              height="auto"
              allDaySlot={false}
              slotMinTime="06:00:00"
              slotMaxTime="24:00:00"
              events={calendarEvents}
              eventClick={handleCalendarEventClick}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,listWeek",
              }}
              buttonText={{
                today: t("sessionHub.today"),
                month: t("sessionHub.month"),
                week: t("sessionHub.week"),
                list: t("sessionHub.viewList"),
              }}
            />
          </div>
        )}
      </Card>

      {showCreateSessionModal && (
        <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl bg-surface p-4 shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-line pb-3">
              <div>
                <p className="text-base font-semibold text-ink">
                  {t("sessionHub.pickClassTitle")}
                </p>
                <p className="text-xs text-ink-muted">
                  {t("sessionHub.pickClassHint")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateSessionModal(false)}
                className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.close")}
              </button>
            </div>

            <div className="mt-3 max-h-[360px] space-y-2 overflow-auto pr-1">
              {activeClasses.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line px-3 py-5 text-center text-sm text-ink-muted">
                  {t("sessionHub.noActiveClasses")}
                </p>
              ) : (
                activeClasses.map((classItem) => (
                  <button
                    key={classItem.id}
                    type="button"
                    onClick={() => handleSelectCreateClass(classItem.id)}
                    className="w-full rounded-lg border border-line p-3 text-left transition-colors hover:border-brand-500 hover:bg-brand-50/50 dark:hover:bg-brand-500/10"
                  >
                    <p className="text-sm font-semibold text-ink">
                      {classItem.name}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {t("sessionHub.classCode", {
                        code: classItem.class_code || t("sessionHub.noCode"),
                      })}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {readOnly && selectedSession && (
        <SessionDetailModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </div>
  );
};

export default SessionHub;
